import * as ng from 'angular';
import * as q from 'q';

import * as Types from '@/types';
import { EmailApi } from '@/types';
import { AlertManagerService } from '@/services/alert-manager';
import { FunctionRelayService } from '@/services/function-relay';
import { ModelHelper } from '@/services/model-helper';
import { RequestStatus } from '@/services/rpc-client';
import { EmailRobotService } from './robot';

export class MailboxModelService {
    public static $inject: string[] = [
        '$rootScope',
        '$timeout',
        'alertManager',
        'emailRobot',
        'functionRelay'
    ];

    private imapMailboxCache: any = {};

    constructor(
        private $rootScope: ng.IRootScopeService,
        private $timeout: ng.ITimeoutService,
        private alertManager: AlertManagerService,
        private emailRobot: EmailRobotService,
        private functionRelay: FunctionRelayService
    ) {}

    public getImapMailboxes(accountId: string): q.IPromise<any[]> {
        if (this.imapMailboxCache[accountId] !== undefined) {
            return q.when(ng.copy(this.imapMailboxCache[accountId]));
        }

        const filter = {
            subFilter: [
                { field: 'AccountId', value: accountId },
                { field: 'MailboxType', value: 'ImapMailbox' }
            ],
            subFilterConnective: 'AND'
        };

        return this.list(100, undefined, filter)
        .then(
            (response) => {
                this.imapMailboxCache[accountId] = ng.copy(response.data);
                return response.data;
            }
        );
    }

    public list = (limit?: number, page?: number, filters?: Types.Finding.Filter, sort?: Types.Finding.SortOptions) => {
        sort = sort || {
         field: 'mailboxEmailAddressUnicode',
         order: 'ASC'
        };
        page = page || 1;

        return this.emailRobot.listMailboxes(filters, limit, page, sort)
        .then(ModelHelper.returnListResponse);
    };

    public listWithoutPagination = (limit?: number, page?: number, filters?: Types.Finding.Filter, sort?: Types.Finding.SortOptions) => {
        sort = sort || {
         field: 'mailboxEmailAddressUnicode',
         order: 'ASC'
        };
        page = page || 1;

        return this.emailRobot.listMailboxesWithoutPagination(filters, limit, page, sort)
        .then(ModelHelper.returnListResponse);
    };

    public findOne = (id: string) => {
        return this.emailRobot.listMailboxesWithoutPagination({field: 'mailboxId', value: id}, 10, 1)
        .then(ModelHelper.returnFindOneResponse);
    };

    public findDomainsettingsByDomainName = (domainName: string) => {
        return this.emailRobot.oneDomainSettings(domainName)
            .then(ModelHelper.returnOneResponse);
    };

    public updateDomainsettings = (domainSettings: EmailApi.DomainSettings) => {
        return this.emailRobot.domainSettingsUpdate(domainSettings);
    };

    public findById = (ids: string[]) => {
        const filter = { subFilterConnective: 'OR', subFilter: ids.map(this.idFilter) };
        return this.emailRobot.listMailboxesWithoutPagination(filter, 0, 1)
        .then(ModelHelper.returnFindResponse);
    };

    public findActiveMailAdressesByDomain = (
        domainNameUnicode: string,
        limit: number,
        additionalFilter?: Types.Finding.Filter
    ) => {
        const subFilter: Types.Finding.Filter[] = [
            { field: 'MailboxDomainNameUnicode', value: domainNameUnicode },
            { field: 'MailboxStatus', value: 'active' }
        ];

        if (additionalFilter !== undefined) {
            subFilter.push(additionalFilter);
        }

        return this.emailRobot.listMailboxesWithoutPagination(
            {
                subFilter: subFilter,
                subFilterConnective: 'AND'
            },
            limit,
            1
        )
        .then(ModelHelper.returnFindResponse);
    };

    public findMailAdressesByDomain = (domainNameUnicode: string, limit?: number, additionalFilter?: Types.Finding.Filter) => {
        limit = limit || 10;
        const subFilter: Types.Finding.Filter[] = [
            { field: 'MailboxDomainNameUnicode', value: domainNameUnicode }
        ];

        if (additionalFilter !== undefined) {
            subFilter.push(additionalFilter);
        }

        return this.emailRobot.listMailboxesWithoutPagination(
            {
                subFilter: subFilter,
                subFilterConnective: 'AND'
            },
            limit,
            1
        )
        .then(ModelHelper.returnFindResponse);
    };

    public listByOrganization = (limit?: number, page?: number, filters?: Types.Finding.Filter, sort?: Types.Finding.SortOptions) => {
        sort = sort || {
            field: 'mailboxEmailAddressUnicode',
            order: 'ASC'
        };
        page = page || 1;

        return this.emailRobot.listOrganizationMailboxes(filters, limit, page, sort)
            .then(ModelHelper.returnListResponse);
    };

    public listOrganization = (limit?: number, page?: number, filters?: Types.Finding.Filter, sort?: Types.Finding.SortOptions) => {
        sort = sort || {
            field: 'OrganizationName',
            order: 'ASC'
        };
        page = page || 1;

        return this.emailRobot.listOrganizations(filters, limit, page, sort)
            .then(ModelHelper.returnListResponse);
    };

    public findMailAdressesByOrganizationWithoutPagination = (
        organizationId: string,
        limit?: number,
        additionalFilter?: Types.Finding.Filter
    ) => {
        limit = limit || 10;
        const subFilter: Types.Finding.Filter[] = [
            { field: 'OrganizationId', value: organizationId }
        ];

        if (additionalFilter !== undefined) {
            subFilter.push(additionalFilter);
        }

        return this.emailRobot.listOrganizationMailboxesWithoutPagination(
            {
                subFilter: subFilter,
                subFilterConnective: 'AND'
            },
            limit,
            1
        )
        .then(ModelHelper.returnFindResponse);
    };

    public findMailAdressesByOrganization = (
        organizationId: string,
        limit?: number,
        additionalFilter?: Types.Finding.Filter
    ) => {
        limit = limit || 10;
        const subFilter: Types.Finding.Filter[] = [
            { field: 'OrganizationId', value: organizationId }
        ];

        if (additionalFilter !== undefined) {
            subFilter.push(additionalFilter);
        }

        return this.emailRobot.listOrganizationMailboxes(
            {
                subFilter: subFilter,
                subFilterConnective: 'AND'
            },
            limit,
            1
        ).then(ModelHelper.returnListResponse);
    };

    // TODO PUI-7089: remove permanently parameter
    public delete = (mailboxes: EmailApi.Mailbox[], date?: Date) => {
        const promises = mailboxes.map(
            (mailbox) => {
                return this.deleteOne(mailbox, date);
            });
        return q.all(promises)
        .then(this.refresh(), this.refresh(RequestStatus.FAILED));
    };

    public createMailbox = (
        mailbox: EmailApi.Mailbox,
        password: string,
        owner: string,
        poolId: string,
        autoconfigureDns?: boolean,
        skipDnsCheck?: boolean,
    ) => {
        return this.emailRobot.createMailbox(mailbox, password, owner, poolId, autoconfigureDns, skipDnsCheck)
        .then(this.refresh(), this.refresh(RequestStatus.FAILED))
        .then(this.refreshDeposit(), this.refreshDeposit(RequestStatus.FAILED));
    };

    public createOrganization = (organization: EmailApi.Organization, owner: string) => {
        return this.emailRobot.createOrganization(organization, owner)
            .then(this.refresh(), this.refresh(RequestStatus.FAILED))
            .then(this.refreshDeposit(), this.refreshDeposit(RequestStatus.FAILED));
    };

    public mailboxModifyRestrictions = (
        apiObject: EmailApi.ApiObject,
        owner?: string
    ) => {
        return this.emailRobot.mailboxModifyRestrictions(apiObject, owner)
            .then(this.returnResponse)
            .then(this.refresh(), this.refresh(RequestStatus.FAILED));
    };

    public update = (mailbox: EmailApi.Mailbox, password: string) => {
        return this.emailRobot.updateMailbox(mailbox, password)
        .then(this.refresh(), this.refresh(RequestStatus.FAILED))
        .then(this.refreshDeposit(), this.refreshDeposit(RequestStatus.FAILED));
    };

    public emailCheck = (email: string, owner: string, bundleId: string, productCode: string, skipDnsCheck?: boolean) => {
        // Stale since 3 years, can this go?
        /* if ([undefined, null, ''].includes(email) || !email.includes('@') || !email.includes('.')) {
            return $q.when({
                status: 'emailAddressInvalid',
                verificationCode: null
            });
        }*/
        return this.emailRobot.checkEmailAddress(email, owner, bundleId, productCode, skipDnsCheck)
            .then(this.returnResponse);
    };

    public restore = (mailboxes: EmailApi.Mailbox[]) => {
        const promises = ModelHelper.sequentiallyRequestApi(
            mailboxes,
            'id',
            this,
            'restoreOne',
            /* translationID */'TR_070819-6042e9_TR',
            this.$timeout,
            this.alertManager
        );

        return promises.then(this.refresh(), this.refresh(RequestStatus.FAILED));
    };

    public purgeRestorable = (mailboxId: string) => {
        return this.emailRobot.purgeRestorableMailbox(mailboxId)
        .then(this.refresh(), this.refresh(RequestStatus.FAILED));
    };

    public mailboxDeletionCancel = (mailboxId: string) => {
        return this.emailRobot.mailboxDeletionCancel(mailboxId)
        .then(this.refresh(), this.refresh(RequestStatus.FAILED));
    };

    private refresh = (status: RequestStatus = RequestStatus.SUCCESSFUL) => (result: any) => {
        this.$rootScope.$broadcast('mailboxes.refresh');
        this.imapMailboxCache = {};
        if (status === RequestStatus.SUCCESSFUL) {
            return result;
        } else {
            return q.reject(result);
        }
    };

    private refreshDeposit = (status: RequestStatus = RequestStatus.SUCCESSFUL) => (result: any) => {
        this.functionRelay.call('updateDeposit', {});
        if (status === RequestStatus.SUCCESSFUL) {
            return result;
        } else {
            return q.reject(result);
        }
    };

    private idFilter = (id: string) => {
        return {field: 'mailboxId', value: id};
    };

    private returnResponse = (result: any) => {
        return result.response;
    };

    private deleteOne = (mailbox: EmailApi.Mailbox, date?: Date) => {
        return this.emailRobot.deleteMailbox(mailbox, date);
    };

    private restoreOne = (mailbox: EmailApi.Mailbox) => {
        return this.emailRobot.restoreMailbox(mailbox.id);
    };
}
